import { motion } from "framer-motion";
import { ArrowLeft, Info, Sparkles } from "lucide-react";
import Footer from "@/components/public/Footer";
import { QUOTE_URL } from "@/lib/constants";

export function SubPageLoading({ testId }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-blush" data-testid={testId}>
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-gold border-t-transparent" />
    </div>
  );
}

function SubPageHeader({ prefix }) {
  return (
    <header className="relative z-20 border-b border-gold/30 bg-white/85 backdrop-blur-xl">
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3 sm:px-8">
        <a href="/" className="flex items-center gap-3" data-testid={`${prefix}-brand-link`}>
          <img src="/brand/logo.png" alt="Miss Cupcake Creations logo" className="h-11 w-11 rounded-full ring-1 ring-gold/50 shadow-sm" />
          <span className="hidden sm:block font-serif text-xl font-semibold tracking-tight text-burgundy leading-none">
            Miss Cupcake Creations
            <span className="block font-script text-sm font-medium text-gold">Celebrate it uniquely</span>
          </span>
        </a>
        <div className="flex items-center gap-3">
          <a href="/" data-testid={`${prefix}-back-link`}
             className="flex items-center gap-1.5 rounded-full border border-burgundy/25 px-4 py-2 text-xs sm:text-sm font-medium text-wine transition-all hover:border-gold hover:text-golddeep">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to site
          </a>
          <a href={QUOTE_URL} target="_blank" rel="noopener noreferrer" data-testid={`${prefix}-header-quote-button`}
             className="rounded-full border border-gold bg-gold px-4 py-2 text-xs sm:text-sm font-semibold text-white shadow-[0_8px_24px_-8px_rgba(201,162,39,0.7)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-golddeep">
            Request a Quote
          </a>
        </div>
      </div>
    </header>
  );
}

export function SubPageHero({ prefix, eyebrow, heading, disclaimer, disclaimerNote }) {
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="text-center">
      <p className="font-script text-2xl sm:text-3xl text-gold">{eyebrow}</p>
      <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-burgundy" data-testid={`${prefix}-heading`}>
        {heading}
      </h1>
      <div className="mx-auto mt-6 flex w-56 items-center gap-3" aria-hidden>
        <span className="ornament-line flex-1" />
        <span className="h-2 w-2 rotate-45 bg-gold" />
        <span className="ornament-line flex-1" />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35, duration: 0.7 }}
        className="mx-auto mt-8 max-w-2xl rounded-2xl border-2 border-gold/60 bg-white/90 p-6 text-left shadow-[0_16px_44px_-18px_rgba(201,162,39,0.5)] backdrop-blur"
        data-testid={`${prefix}-disclaimer`}
      >
        <p className="flex items-start gap-3 text-sm sm:text-base leading-relaxed text-plum/85">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
          <span>{disclaimer}</span>
        </p>
        {disclaimerNote && <p className="mt-4 pl-8 text-xs sm:text-sm italic text-plum/60">{disclaimerNote}</p>}
      </motion.div>
    </motion.div>
  );
}

export function SubPageCta({ prefix, script, label }) {
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }} className="mt-10 text-center">
      <p className="font-script text-2xl text-gold">{script}</p>
      <a
        href={QUOTE_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`${prefix}-quote-button`}
        className="mt-4 inline-flex items-center gap-2 rounded-full border border-gold bg-gold px-9 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-white shadow-[0_14px_36px_-10px_rgba(201,162,39,0.8)] transition-all duration-300 hover:-translate-y-1 hover:bg-golddeep"
      >
        <Sparkles className="h-4 w-4" /> {label}
      </a>
    </motion.div>
  );
}

export default function SubPageLayout({ prefix, pageTestId, maxWidth = "max-w-5xl", children }) {
  return (
    <div className="relative min-h-screen bg-blush text-plum font-sans overflow-x-clip" data-testid={pageTestId}>
      <div aria-hidden className="pointer-events-none fixed inset-0 z-0 opacity-25 mix-blend-multiply bg-cover bg-top" style={{ backgroundImage: "url(/brand/watercolour.jpg)" }} />
      <img src="/brand/floral-tl.png" alt="" aria-hidden className="pointer-events-none fixed left-0 top-0 z-0 w-52 sm:w-72 opacity-90" />
      <img src="/brand/floral-br.png" alt="" aria-hidden className="pointer-events-none fixed bottom-0 right-0 z-0 w-56 sm:w-80 opacity-90" />
      <SubPageHeader prefix={prefix} />
      <main className={`relative z-10 mx-auto ${maxWidth} px-4 py-16 sm:px-8 sm:py-24`}>{children}</main>
      <Footer />
    </div>
  );
}
